import React, { useCallback, useMemo, useState } from 'react';
import type { Dimension } from '../BuyForm/BuyForm.types';
import { DIM_PREFIX } from '../BuyForm/useBuyForm';
import { SwatchColorGrid } from '@apps/registry/common/components/SwatchColorGrid';
import { ProductVariantFormField } from '../ProductVariantFormField';
import { SwatchColorCarousel } from '@apps/registry/common/components/SwatchColorCarousel';
import { ProductVariantStringList } from '../ProductVariantStringList';
import { GroupDimensionSwatchType } from '@graphql/generated-base';
import type { SwatchColorOption } from '@apps/registry/common/components/SwatchColorGrid/types';
import type { FieldInputProps } from 'formik';

interface BuyFormDimensionCommonProps
  extends Readonly<{
    colorSwatchVariant: 'grid' | 'carousel';
    showAllDimensions: boolean;
    isQuickAddForm: boolean | undefined;
    getFieldProps: (nameOrOptions: string | undefined) => FieldInputProps<string | undefined>;
    setDimensionOption: (optionValue: string, updatedField: string) => void;
    checkOptionAvailability: (optionId: string, dimensionId: string | undefined) => boolean;
    handleHover: (optionValue: string, updatedField: string, isHoverExit?: boolean) => void;
    shouldHideDiscontinuedOptions?: boolean;
  }> {}

interface BuyFormDimensionsListProps
  extends BuyFormDimensionCommonProps,
    Readonly<{
      dimensions: Dimension[];
    }> {}

export const BuyFormDimensionsList: React.FC<BuyFormDimensionsListProps> = ({
  dimensions,
  colorSwatchVariant,
  showAllDimensions,
  isQuickAddForm,
  getFieldProps,
  setDimensionOption,
  checkOptionAvailability,
  handleHover,
  shouldHideDiscontinuedOptions,
}) => {
  return (
    <>
      {dimensions.map(dimension => (
        <BuyFormDimension
          key={dimension.id}
          dimension={dimension}
          colorSwatchVariant={colorSwatchVariant}
          showAllDimensions={showAllDimensions}
          isQuickAddForm={isQuickAddForm}
          getFieldProps={getFieldProps}
          setDimensionOption={setDimensionOption}
          checkOptionAvailability={checkOptionAvailability}
          handleHover={handleHover}
          shouldHideDiscontinuedOptions={shouldHideDiscontinuedOptions}
        />
      ))}
    </>
  );
};

interface BuyFormDimensionProps
  extends BuyFormDimensionCommonProps,
    Readonly<{
      dimension: Dimension;
    }> {}

const BuyFormDimension: React.FC<BuyFormDimensionProps> = ({
  dimension,
  colorSwatchVariant,
  showAllDimensions,
  isQuickAddForm,
  getFieldProps,
  setDimensionOption,
  checkOptionAvailability,
  handleHover,
  shouldHideDiscontinuedOptions,
}) => {
  const selectedOptionId = getFieldProps(`${DIM_PREFIX}${dimension.id}`).value;
  const displayOptions = useMemo(
    () => (shouldHideDiscontinuedOptions ? dimension.options.filter(option => checkOptionAvailability(option.id, dimension.id)) : dimension.options),
    [dimension.options, dimension.id, shouldHideDiscontinuedOptions, checkOptionAvailability],
  );
  const selectedOptionLabel = useMemo(() => displayOptions.find(option => option.id === selectedOptionId)?.label, [displayOptions, selectedOptionId]);
  const [optionLabel, setOptionLabel] = useState(selectedOptionLabel);

  const handleOptionHover = useCallback((_e: React.MouseEvent, swatch: SwatchColorOption) => {
    setOptionLabel(swatch.label);
  }, []);
  const handleOptionOut = useCallback(
    (_e: React.MouseEvent, _swatch: SwatchColorOption) => {
      setOptionLabel(selectedOptionLabel);
    },
    [selectedOptionLabel],
  );

  if (dimension.swatchType === GroupDimensionSwatchType.color || dimension.swatchType === GroupDimensionSwatchType.image) {
    if (displayOptions?.length > 1) {
      return (
        <ProductVariantFormField
          key={dimension.label}
          label={`${dimension.label}:`}
          helperText={optionLabel}
          error={false}
        >
          {colorSwatchVariant === 'grid' ? (
            <SwatchColorGrid
              id={dimension.id}
              name={dimension.label}
              value={selectedOptionId}
              columns={6}
              swatchSize={8}
              options={displayOptions}
              onInteract={(_e: React.SyntheticEvent, color: SwatchColorOption) => setDimensionOption(color.id, dimension.id)}
              interactionType={'click'}
              disableOptionOverflow={showAllDimensions}
              checkAvailability={true}
              checkOptionAvailability={checkOptionAvailability}
              onSwatchHover={(_e, color: SwatchColorOption) => handleHover(color.id, dimension.id, false)}
              onSwatchHoverExit={(_e, color: SwatchColorOption) => handleHover(color.id, dimension.id, true)}
            />
          ) : (
            <SwatchColorCarousel
              id={dimension.id}
              name={dimension.label}
              value={selectedOptionId}
              swatchSize={24}
              options={displayOptions}
              onInteract={(color: SwatchColorOption) => setDimensionOption(color.id, dimension.id)}
              onSwatchHover={handleOptionHover}
              onSwatchOut={handleOptionOut}
              interactionType={'click'}
              checkAvailability={true}
              checkOptionAvailability={checkOptionAvailability}
            />
          )}
        </ProductVariantFormField>
      );
    }
    return null;
  }

  return (
    <ProductVariantFormField
      key={dimension.label}
      label={`${dimension.label}:`}
      helperText={optionLabel}
      error={false}
    >
      <ProductVariantStringList
        id={dimension.id}
        name={dimension.label}
        options={displayOptions}
        value={selectedOptionId}
        variant="pill"
        onChange={dimensionOptionId => {
          setDimensionOption(dimensionOptionId ?? '', dimension.id);
        }}
        checkAvailability={true}
        isQuickAddForm={isQuickAddForm}
        checkOptionAvailability={checkOptionAvailability}
        size={'sm'}
      />
    </ProductVariantFormField>
  );
};
